import type { ReactNode } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { DetailList } from './DetailList';
import { readObject, readString } from '../utils/snapshotReaders';
import type { RegistrationSource } from '../hooks/useAgreementVehicleRegistration';
import type { RegistrationView } from '../utils/registrationView';
import type { Snapshot } from '../types/agreements.types';

interface Props {
    snapshot: Snapshot;
    registration: RegistrationView;
    source: RegistrationSource;
    canChangeRegistration: boolean;
    onChangeRegistration: () => void;
    /** The vehicle's amendment log; omitted for a caller who may not read the vehicle. */
    log?: ReactNode;
}

const headingId = 'agreement-vehicle-heading';

/** `vehicle_snapshot` arrives in either the flat shape or the nested
 *  `derivative` shape; every field falls back from nested to flat. */
export function AgreementVehiclePanel({
    snapshot,
    registration,
    source,
    canChangeRegistration,
    onChangeRegistration,
    log,
}: Props) {
    const derivative = readObject(snapshot.derivative);
    const make = readObject(derivative?.make) ?? readObject(snapshot.make);
    const model = readObject(derivative?.model) ?? readObject(snapshot.model);

    const makeModel = [readString(make?.name), readString(model?.name)].filter(Boolean).join(' ') || undefined;
    const shortName = (derivative ? readString(derivative.short_name) : undefined) ?? readString(snapshot.short_name);

    return (
        <Card>
            <CardHeader className="flex items-center justify-between gap-4">
                <CardTitle>
                    <span id={headingId}>Vehicle</span>
                </CardTitle>
                {canChangeRegistration && (
                    <PermissionGuard permission={{ resource: 'vehicle', action: 'change_registration' }}>
                        <Button type="button" size="xs" variant="secondary" onClick={onChangeRegistration}>
                            Change registration
                        </Button>
                    </PermissionGuard>
                )}
            </CardHeader>
            <CardContent>
                <section aria-labelledby={headingId} aria-busy={source === 'pending'}>
                    <DetailList
                        emptyMessage="No vehicle details were recorded on this agreement."
                        fields={[
                            { label: 'Make / model', value: makeModel },
                            { label: 'Derivative', value: shortName },
                            { label: 'VRM', value: registration.current },
                            { label: 'VIN', value: readString(snapshot.vin) },
                            { label: 'Colour', value: readString(snapshot.colour) },
                            {
                                label: 'First registered',
                                value: readString(snapshot.date_of_first_registration),
                            },
                        ]}
                    />
                    {registration.asSold && (
                        <p className="mt-2 text-xs text-mp-muted">
                            As sold: {registration.asSold}
                        </p>
                    )}
                    {/* Only when true: 'pending' must not claim which record is shown. */}
                    {source === 'snapshot' && (
                        <p className="mt-2 text-xs text-mp-muted">
                            Showing the registration recorded at sale
                        </p>
                    )}
                </section>
                {log && <div className="mt-6 border-t border-mp-border pt-4">{log}</div>}
            </CardContent>
        </Card>
    );
}
